import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Listing, listingsAPI } from '../api';
import SellerBadge from './SellerBadge';
import ListingImage from './ListingImage';
import { createSlug } from '../utils/slug';
import { cardDisplayPrice, formatListingPrice, strikethroughListPrice } from '../utils/listingPricing';
import { getCachedListingCardImage, ListingDetailNavState, prefetchListingDetail } from '../utils/listingImageCache';
import { listingImageUrl } from '../utils/listingImageUrl';

interface ListingCardProps {
  listing: Listing;
  addButton?: React.ReactNode;
  showOpenStatus?: boolean; // Control whether to show Open/Closed status
  showBadges?: boolean; // Control whether to show seller/store badges
  showLocation?: boolean; // Control whether to show location instead of seller/store name
  /** When set, show distance on the card */
  distanceMiles?: number | null;
  /** Show distance in the footer row (e.g. search page) instead of open/closed */
  showDistanceFooter?: boolean;
  /** Load image immediately (first row on home page) */
  priority?: boolean;
}

const ListingCard: React.FC<ListingCardProps> = ({
  listing,
  addButton,
  showOpenStatus = true,
  showBadges = true,
  showLocation = true,
  distanceMiles,
  showDistanceFooter = false,
  priority = false,
}) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const navigate = useNavigate();
  const linkRef = useRef<HTMLAnchorElement>(null);
  const buttonContainerRef = useRef<HTMLDivElement>(null);
  const cardClickableRef = useRef<HTMLDivElement>(null);
  
  const formatTime = (time: string) => {
    if (!time) return '';
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  const isStoreClosed = () => {
    if (!listing.store_close_time) return false;
    const now = new Date();
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();
    const [closeHour, closeMinute] = listing.store_close_time.split(':').map(Number);
    const currentTime = currentHour * 60 + currentMinute;
    const closeTime = closeHour * 60 + closeMinute;
    return currentTime >= closeTime;
  };

  const isSold = listing.sold === 1 || listing.sold === true;

  const displayPrice = cardDisplayPrice(listing);
  const originalPrice = strikethroughListPrice(listing);
  const hasDistance = distanceMiles != null && !Number.isNaN(distanceMiles);
  const showInlineDistance = hasDistance && !showDistanceFooter;
  const showFooterDistance = hasDistance && showDistanceFooter;

  return (
    <div className="block h-full group/card relative">
      <div 
        ref={cardClickableRef}
        className="block h-full cursor-pointer relative"
        onPointerDown={() => {
          if (listing?.id) {
            prefetchListingDetail(listing.id, (id) => listingsAPI.getOne(id)).catch(() => {});
          }
        }}
        onClick={(e) => {
          const target = e.target as HTMLElement;
          const button = target.closest('.add-to-cart-button') || 
                        target.closest('.add-to-cart-button-container') ||
                        target.closest('[data-add-to-cart="true"]');
          if (button || target.classList.contains('add-to-cart-button')) {
            e.stopPropagation();
            e.preventDefault();
            return false;
          }
          const images =
            listing.image_urls && listing.image_urls.length > 0
              ? listing.image_urls
              : listing.image_url
                ? [listing.image_url]
                : [];
          const previewOriginal = images[currentImageIndex] || images[0] || '';
          const navState: ListingDetailNavState = {
            previewImageUrl:
              getCachedListingCardImage(previewOriginal) ||
              listingImageUrl(previewOriginal, 'card') ||
              previewOriginal,
            listing,
          };
          prefetchListingDetail(listing.id, (id) => listingsAPI.getOne(id)).catch(() => {});
          navigate(`/listings/${listing.id}`, { state: navState });
        }}
      >
        <div className={`bg-white rounded-xl overflow-hidden relative border border-gray-200 transition-all duration-300 h-full flex flex-col ${isSold ? 'opacity-60' : ''}`} style={{ overflow: 'hidden', position: 'relative' }}>
        {isSold && (
          <div className="absolute top-2 right-2 z-10 bg-red-500 text-white px-2.5 py-1 rounded-full font-bold text-xs shadow-lg">
            SOLD
          </div>
        )}
        <div className="relative w-full aspect-[4/3] bg-gray-50 overflow-hidden">
          {(() => {
            const images = listing.image_urls && listing.image_urls.length > 0 
              ? listing.image_urls 
              : listing.image_url 
                ? [listing.image_url] 
                : [];
            
            return (
              <>
                {images.length === 0 ? (
                  <div className={`absolute inset-0 flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200 ${isSold ? 'opacity-50' : ''}`}>
                    <i className="ri-image-line text-gray-300 text-5xl sm:text-6xl"></i>
                  </div>
                ) : (
                  <>
                    <ListingImage
                      src={images[currentImageIndex]}
                      size="card"
                      alt={`${listing.title} - Image ${currentImageIndex + 1}`}
                      className={`absolute inset-0 w-full h-full object-cover object-center transition-transform duration-300 ease-out group-hover/card:scale-110 ${isSold ? 'grayscale' : ''}`}
                      loading={priority ? 'eager' : 'lazy'}
                      decoding="async"
                      fetchPriority={priority ? 'high' : 'auto'}
                    />
                    {isSold && (
                      <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center">
                        <span className="text-white text-xl font-bold">SOLD</span>
                      </div>
                    )}
                  </>
                )}
                {addButton && (
                  <div 
                    ref={buttonContainerRef}
                    className="absolute bottom-2 right-2 z-10 add-to-cart-button-container"
                    style={{ pointerEvents: 'auto' }}
                  >
                    {addButton}
                  </div>
                )}
              </>
            );
          })()}
        </div>
        <div className="p-3 sm:p-4 flex-1 flex flex-col min-w-0 overflow-hidden">
          <div className="mb-2 flex-1 min-w-0 overflow-hidden" style={{ paddingBottom: '1px' }}>
            <h3 className={`text-sm sm:text-base font-semibold leading-tight ${isSold ? 'text-gray-400' : 'text-gray-900'} min-w-0`} style={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              minHeight: '2.5rem',
              wordBreak: 'break-word',
              maxWidth: '100%',
              lineHeight: '1.4',
              marginTop: '-1px',
              paddingTop: '1px'
            }}>
              {listing.nearby_cities ? (
                <>
                  {listing.title} – {listing.nearby_cities.primary}{listing.nearby_cities.nearby.length > 0 ? ' / ' + listing.nearby_cities.nearby.slice(0, 2).join(' / ') : ''}
                </>
              ) : (
                listing.title
              )}
            </h3>
          </div>
          <div className="flex items-center justify-between mb-2 gap-2 min-w-0">
            <div className="flex items-center gap-1.5 sm:gap-2 min-w-0 flex-wrap">
              <span className={`text-base sm:text-xl font-bold shrink-0 ${isSold ? 'text-gray-400 line-through' : 'text-gray-900'}`}>
                {formatListingPrice(displayPrice)}
              </span>
              {originalPrice != null && !isSold && (
                <span className="text-sm sm:text-base text-gray-500 line-through">
                  {formatListingPrice(originalPrice)}
                </span>
              )}
              {/* Item Rating - Star-based (like Etsy) */}
              {(Number(listing.quantity) || 1) > 1 && listing.item_rating && listing.item_rating.total_ratings > 0 && (
                <div className="flex items-center gap-1 text-xs text-gray-600" title="Item rating">
                  <i className="ri-star-fill text-yellow-400"></i>
                  <span className="font-medium">
                    {listing.item_rating.average_rating?.toFixed(2) || '0.00'}
                  </span>
                  <span className="text-gray-400">
                    ({listing.item_rating.total_ratings})
                  </span>
                </div>
              )}
            </div>
          </div>
          <div className="flex flex-col gap-1 mt-auto">
            <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
              {showLocation ? (
                <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 min-w-0">
                  <span className="text-gray-600 truncate min-w-0">
                    {listing.city_name}{listing.state_abbreviation ? `, ${listing.state_abbreviation}` : ''}
                  </span>
                  {showInlineDistance && (
                    <span className="text-gray-500 flex-shrink-0 text-[11px] sm:text-xs whitespace-nowrap" title={`${Number(distanceMiles).toFixed(1)} miles away`}>
                      · {Number(distanceMiles).toFixed(1)} <span className="sm:hidden">mi</span><span className="hidden sm:inline">miles away</span>
                    </span>
                  )}
                </div>
              ) : listing.store_id && listing.store_name ? (
                <div className="relative group flex-1 mr-2 min-w-0">
                  <Link
                    to={`/stores/${createSlug(listing.store_name)}`}
                    onClick={(e) => e.stopPropagation()}
                    onMouseEnter={(e) => {
                      const tooltip = e.currentTarget.nextElementSibling as HTMLElement;
                      if (tooltip) {
                        tooltip.style.opacity = '1';
                        tooltip.style.visibility = 'visible';
                      }
                    }}
                    onMouseLeave={(e) => {
                      const tooltip = e.currentTarget.nextElementSibling as HTMLElement;
                      if (tooltip) {
                        tooltip.style.opacity = '0';
                        tooltip.style.visibility = 'hidden';
                      }
                    }}
                    className="hover:text-green-600 transition font-medium truncate block overflow-hidden flex items-center gap-1.5"
                  >
                    <i className="ri-store-2-fill text-green-600 flex-shrink-0" style={{ fontSize: '14px' }}></i>
                    <span className="hidden sm:inline">{listing.store_name}</span>
                    <span className="sm:hidden">{listing.store_name.length > 10 ? listing.store_name.substring(0, 10) + '...' : listing.store_name}</span>
                  </Link>
                  {/* Rating Tooltip on Hover */}
                  {listing.store_badge && listing.store_badge.total_ratings > 0 && (
                    <div 
                      className="absolute bottom-full left-0 mb-2 z-50 w-48 bg-gray-800 text-white rounded-lg shadow-xl p-3 pointer-events-none transition-all duration-200"
                      style={{ opacity: 0, visibility: 'hidden' }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.opacity = '1';
                        e.currentTarget.style.visibility = 'visible';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.opacity = '0';
                        e.currentTarget.style.visibility = 'hidden';
                      }}
                    >
                      <div className="absolute -bottom-2 left-4 w-4 h-4 bg-gray-800 transform rotate-45"></div>
                      <div className="text-sm relative z-10">
                        <div className="font-semibold mb-2">{listing.store_name}</div>
                        <div className="flex items-center space-x-2">
                          <div className="flex items-center">
                            <i className="ri-star-fill text-yellow-300 mr-1"></i>
                            <span className="text-sm">{listing.store_badge.positive_percentage}%</span>
                          </div>
                          <span className="text-gray-400">({listing.store_badge.formatted_count} ratings)</span>
                        </div>
                        {listing.store_badge.is_top_rated_plus && (
                          <div className="flex items-center space-x-1 mt-2">
                            <i className="ri-store-2-fill text-yellow-300 text-xs"></i>
                            <span className="text-xs text-purple-300">Top Store Plus</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="relative group flex-1 mr-2 min-w-0">
                  <Link
                    to={`/sellers/${listing.user_id}`}
                    onClick={(e) => e.stopPropagation()}
                    onMouseEnter={(e) => {
                      const tooltip = e.currentTarget.nextElementSibling as HTMLElement;
                      if (tooltip) {
                        tooltip.style.opacity = '1';
                        tooltip.style.visibility = 'visible';
                      }
                    }}
                    onMouseLeave={(e) => {
                      const tooltip = e.currentTarget.nextElementSibling as HTMLElement;
                      if (tooltip) {
                        tooltip.style.opacity = '0';
                        tooltip.style.visibility = 'hidden';
                      }
                    }}
                    className="hover:text-green-600 transition truncate block overflow-hidden"
                  >
                    {listing.seller_name}
                  </Link>
                  {/* Ratings Tooltip on Hover */}
                  {listing.seller_badge && listing.seller_badge.total_ratings > 0 && (
                    <div 
                      className="absolute bottom-full left-0 mb-2 z-50 w-48 bg-gray-800 text-white rounded-lg shadow-xl p-3 pointer-events-none transition-all duration-200"
                      style={{ opacity: 0, visibility: 'hidden' }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.opacity = '1';
                        e.currentTarget.style.visibility = 'visible';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.opacity = '0';
                        e.currentTarget.style.visibility = 'hidden';
                      }}
                    >
                      <div className="absolute -bottom-2 left-4 w-4 h-4 bg-gray-800 transform rotate-45"></div>
                      <div className="text-sm relative z-10">
                        <div className="font-semibold mb-2">{listing.seller_name}</div>
                        {listing.seller_badge.is_top_rated_plus && (
                          <div className="flex items-center space-x-1 mt-2">
                            <i className="ri-star-fill text-yellow-300 text-xs"></i>
                            <span className="text-xs text-purple-300">Top Rated Plus</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            {/* Seller Badge */}
            {showBadges && !listing.store_id && listing.seller_badge && listing.seller_badge.total_ratings > 0 && (
              <div className="mb-1" onClick={(e) => e.stopPropagation()}>
                <SellerBadge
                  username=""
                  positivePercentage={listing.seller_badge.positive_percentage}
                  totalRatings={listing.seller_badge.total_ratings}
                  formattedCount={listing.seller_badge.formatted_count}
                  isTopRatedPlus={listing.seller_badge.is_top_rated_plus}
                  hasFastShipping={listing.seller_badge.has_fast_shipping}
                  hasTracking={listing.seller_badge.has_tracking}
                  returnsMoneyBack={listing.seller_badge.returns_money_back || false}
                  showFullBadge={false}
                />
              </div>
            )}
            {showFooterDistance && (
              <div className="flex items-center justify-end text-xs text-gray-500">
                <span>{Math.round(Number(distanceMiles))} mi</span>
              </div>
            )}
            {showOpenStatus && !showFooterDistance && listing.store_id && listing.store_close_time && (
              <div className="flex items-center justify-between text-xs">
                {isStoreClosed() ? (
                  <span className="text-red-600 font-medium">🔴 Closed</span>
                ) : (
                  <span className="text-green-600 font-medium">🟢 Open</span>
                )}
                <span className="text-gray-500">Closes {formatTime(listing.store_close_time)}</span>
              </div>
            )}
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default ListingCard;

